import { useState, useCallback, useEffect } from 'react';

/**
 * GA4 window loader — fetches available windows and auto-selects the first one.
 * When startDate/endDate are provided, windows are filtered to those overlapping
 * that range (avoids putting all ~616 of a client's windows in a picker). The
 * `fn_get_ga4_*_windows` RPCs take no date params, so that filtering happens in
 * the `api/auth.js` wrapper, not server-side.
 *
 * Optimistic initial selection: if `initialGuess` is provided, `selected` is seeded
 * with it before the window list resolves — so downstream data fetches can fire in
 * parallel with the window list (saves 1 RTT on the happy path). When the real list
 * returns, the guess is kept if still valid; otherwise it's replaced with the first
 * real window, which triggers a downstream refetch.
 *
 * @param {Function} apiFn        – async (clientId, { startDate, endDate }) => { data: [{ date_start, date_end }] }
 * @param {string}   clientId     – client identifier
 * @param {Object}   [opts]
 * @param {boolean}  [opts.skip]         – skip fetching when true
 * @param {string}   [opts.startDate]    – ISO date — only keep windows overlapping >= startDate
 * @param {string}   [opts.endDate]      – ISO date — only keep windows overlapping <= endDate
 * @param {Object}   [opts.initialGuess] – { date_start, date_end } seed for `selected` (optimistic render)
 * @returns {{ windows, selected, setSelected, loading, error }}
 */
const useGA4Windows = (apiFn, clientId, { skip = false, startDate, endDate, initialGuess } = {}) => {
  // Only seed the optimistic guess once we can actually fetch — otherwise `selected`
  // goes truthy before a client exists and downstream fetches fire with client_id=null.
  const canFetch = !skip && !!clientId;
  const [windows, setWindows] = useState([]);
  const [selected, setSelected] = useState(canFetch ? (initialGuess || null) : null);
  const [loading, setLoading] = useState(canFetch);
  const [error, setError] = useState('');

  // Re-seed with the guess when its dates change (e.g., user picks a new date range),
  // but only once fetching is unblocked (client resolved and not skipped).
  useEffect(() => {
    if (!canFetch) return;
    if (initialGuess?.date_start && initialGuess?.date_end) {
      setSelected((prev) => {
        if (prev && prev.date_start === initialGuess.date_start && prev.date_end === initialGuess.date_end) return prev;
        return initialGuess;
      });
    }
  }, [canFetch, initialGuess?.date_start, initialGuess?.date_end]);

  const load = useCallback(async () => {
    if (skip || !clientId) { setLoading(false); return; }
    setLoading(true);
    try {
      const resp = await apiFn(clientId, { startDate, endDate });
      const w = resp?.data ?? [];
      setWindows(w);
      setError('');
      // If the previous selection (guess or user pick) is still in the new filtered list, keep it.
      // Otherwise, fall back to the first window — this triggers a downstream refetch.
      //
      // Empty list is NOT "no window". It used to be the *normal* case, when the
      // `*Windows` helpers were `[]` stubs; as of 2026-07-30 they call the real
      // `fn_get_ga4_*_windows` RPCs, so a populated list is the happy path and an
      // empty one means the client genuinely has no GA4 window overlapping the
      // selected range. Either way we keep the optimistic `initialGuess` (the
      // latest 7-day snapshot) as `selected` so window-scoped sections
      // (geo/pages/browsers/OS/hourly/DoW + the geo city drill-down) still fetch
      // and render — nulling `selected` here would skip every one of those fetches.
      setSelected((prev) => {
        if (w.length === 0) return prev ?? initialGuess ?? null;
        const stillValid = prev && w.some(x => x.date_start === prev.date_start && x.date_end === prev.date_end);
        return stillValid ? prev : w[0];
      });
    } catch (e) {
      // Surface the failure so the consumer's panel can render an error branch
      // rather than an indistinguishable "no windows" empty.
      setWindows([]);
      setSelected(null);
      setError(e?.message || 'Failed to load analysis windows');
    } finally {
      setLoading(false);
    }
  }, [apiFn, clientId, skip, startDate, endDate, initialGuess?.date_start, initialGuess?.date_end]);

  useEffect(() => { load(); }, [load]);

  return { windows, selected, setSelected, loading, error };
};

export default useGA4Windows;
